import React, { useEffect, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import { updateUserWidgetPrefs } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

// 🔄 Individual widget row
const WidgetRow = ({ widget, index, onToggle }) => {
  return (
    <li className="list-group-item d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center gap-3 flex-grow-1">
        <span className="me-2 grab-handle" style={{ cursor: "grab" }}>
          ☰
        </span>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id={`toggle-${widget.widget_type}`}
            checked={!!widget.is_visible}
            onChange={(e) => onToggle(widget.widget_type, e.target.checked)}
          />
          <label
            className="form-check-label ms-2"
            htmlFor={`toggle-${widget.widget_type}`}
          >
            {widget.label}
          </label>
        </div>
      </div>
      <span className="text-muted">#{index + 1}</span>
    </li>
  );
};

// ⚙️ Main component - removed userId prop
const CustomizeDashboardWidgets = ({
  availableWidgets,
  currentPrefs,
  onClose,
  onSaved,
}) => {
  const { user } = useAuth();
  const [widgets, setWidgets] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isLoading =
    !Array.isArray(currentPrefs) || !Array.isArray(availableWidgets);

  const savePreferences = async () => {
    try {
      const payload = {
        widgets: widgets.map((w, index) => ({
          widget_type: w.widget_type,
          is_visible: w.is_visible,
          sort_order: index + 1,
        })),
      };

      // No userId in URL - backend gets from JWT
      await updateUserWidgetPrefs(payload);

      if (onSaved) {
        await onSaved();
      }

      toast.success("✅ Preferences saved successfully!");
      setHasUnsavedChanges(false);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to save widget preferences", err);
      }
      toast.error("Failed to save preferences.");
    }
  };

  useEffect(() => {
    if (isLoading || hasInitialized) return;

    const map = {};
    currentPrefs.forEach((p) => {
      if (p?.widget_type) {
        const key = p.widget_type.trim().toLowerCase();
        map[key] = {
          ...p,
          key,
        };
      }
    });

    const built = availableWidgets
      .filter((w) => !w.adminOnly || user?.is_admin)
      .map((w, index) => {
        const cleanId = w.id?.trim().toLowerCase();
        const match = map[cleanId];

        return {
          widget_type: cleanId,
          label: w.label || "Unknown Widget",
          is_visible: match?.is_visible ?? false,
          sort_order: match?.sort_order ?? index,
        };
      })
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    setWidgets(built);
    setHasInitialized(true);
  }, [
    isLoading,
    hasInitialized,
    availableWidgets,
    currentPrefs,
    user?.is_admin,
  ]);

  const handleToggle = (widget_type, newValue) => {
    const updated = widgets.map((w) =>
      w.widget_type === widget_type ? { ...w, is_visible: newValue } : w
    );
    setWidgets(updated);
    setHasUnsavedChanges(true);
  };

  return (
    <div className="card p-3 mt-4">
      <h5>⚙️ Customize Dashboard Widgets</h5>

      {isLoading ? (
        <div className="alert alert-warning">Loading widget preferences...</div>
      ) : widgets.length === 0 ? (
        <p className="text-muted mt-3">No widgets available.</p>
      ) : (
        <ul className="list-group mt-3">
          <ReactSortable
            list={widgets}
            setList={(newList) => {
              setWidgets(newList);
              setHasUnsavedChanges(true);
            }}
            handle=".grab-handle"
            animation={150}
          >
            {widgets.map((w, idx) => (
              <WidgetRow
                key={w.widget_type}
                widget={w}
                index={idx}
                onToggle={handleToggle}
              />
            ))}
          </ReactSortable>
        </ul>
      )}

      <div className="d-flex justify-content-end mt-3 gap-2">
        <button
          className="btn btn-primary"
          onClick={savePreferences}
          disabled={!hasUnsavedChanges}
        >
          Save
        </button>
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default CustomizeDashboardWidgets;
